import {
  FrappeApiHelper,
  mappingBuilderWithDefaultMappings,
  mappingHelper,
} from "@aht/frappe-client";
import { Injectable } from "@angular/core";
import { Chapter } from "../models/instruction/chapter";
import { map, Observable } from "rxjs";
import { ChapterConfirmation } from "../models/instruction/chapter-confirmation";
import { Answer, PossibleAnswer, Question } from "../models/question";
import { questions } from "../ngrx/selectors/instruction-details.selector";

@Injectable({
  providedIn: "root",
})
export class ChapterService {
  constructor(private readonly helper: FrappeApiHelper) {}

  private chapterMapping = mappingBuilderWithDefaultMappings<Chapter>(
    "SAR Chapter",
    [
      mappingHelper.stringMapper("title"),

      mappingHelper.stringMapper("instruction"),
      mappingHelper.intMapper("order"),
      mappingHelper.booleanMapper("post_processing_done", "postProcessingDone"),
      mappingHelper.stringMapper(
        "post_processed_mime_type",
        "postProcessedMimeType",
      ),
      mappingHelper.stringMapper("download_link", "downloadLink"),
      mappingHelper.stringMapper("filename"),
      mappingHelper.stringMapper("mime_type", "mimeType"),
    ],
  );

  private chapterConfirmationMapping =
    mappingBuilderWithDefaultMappings<ChapterConfirmation>(
      "SAR Chapter Confirmation",
      [
        mappingHelper.stringMapper("chapter"),
        mappingHelper.stringMapper("user"),
        mappingHelper.stringMapper("instruction_confirmation"),
      ],
    );
  private possibleAnswerMapping =
    mappingBuilderWithDefaultMappings<PossibleAnswer>("SAR Question Answer", [
      mappingHelper.stringMapper("answer"),
    ]);

  private questionMapping = mappingBuilderWithDefaultMappings<Question>(
    "SAR Question",
    [
      mappingHelper.stringMapper("chapter"),
      mappingHelper.stringMapper("question"),
      mappingHelper.listMapperWithItemConversion(
        this.possibleAnswerMapping.conversion(),
        "answers",
        "possibleAnswers",
      ),
    ],
  );

  loadChapters(instruction: string): Observable<Chapter[]> {
    const params = new Map<string, string>();
    params.set("instruction", instruction);

    return this.helper
      .callWithResults(this.chapterMapping.conversion(), {
        method:
          "sar.controllers.chapter_controller.get_chapters_for_instruction",
        extraParams: params,
        type: "GET",
      })
      .pipe(map((chapters) => chapters.sort((a, b) => a.order - b.order)));
  }

  loadChapterConfirmations(
    instructionConfirmation: string,
  ): Observable<ChapterConfirmation[]> {
    const params = new Map<string, string>();

    params.set("instruction_confirmation", instructionConfirmation);

    return this.helper.callWithResults(
      this.chapterConfirmationMapping.conversion(),
      {
        method:
          "sar.controllers.chapter_controller.get_chapters_confirmations_for_instruction",
        extraParams: params,
        type: "GET",
      },
    );
  }

  confirmChapter(
    chapter: string,
    instructionConfirmation: string,
    answers: Answer[],
  ): Observable<ChapterConfirmation> {
    return this.helper.callWithResult(
      this.chapterConfirmationMapping.conversion(),
      {
        method: "sar.controllers.chapter_controller.confirm_chapter",
        body: {
          chapter: chapter,
          instruction_confirmation: instructionConfirmation,
          answers: answers.map((a) => ({
            question: a.question,
            answers: a.answers,
          })),
        },
        type: "POST",
      },
    );
  }

  loadQuestionsForChapter(
    chapter: string,
    instructionConfirmation: string,
  ): Observable<Question[]> {
    const params = new Map<string, string>();
    params.set("instruction_confirmation", instructionConfirmation);
    params.set("chapter", chapter);

    return this.helper.callWithResults(this.questionMapping.conversion(), {
      method: "sar.controllers.chapter_controller.get_questions_for_chapter",
      type: "GET",
      extraParams: params,
    });
  }
}
